import { useState, useEffect, useRef } from "react";
import { message } from "antd";

const isCompanySelected = (company) => company.id.trim() !== "";

export const useGetSpecificDataCompanyHook = (
	company, 
	fetchFunction, 
	token, 
	createFunction,
	{ // Extra Atrributes
		aditionalParamsForFetch = {},
		entityKey = null,
		specialFilterFunction = null
	} = {}
) => {
	const [data, setData] = useState([]);
	const [backupData, setBackupData] = useState([]);
	const [loading, setLoading] = useState(false);

	// Local data storage per entity type (if entityKey is provided)
	const localData = useRef({});

	const addNewData = async (newData, extraData = {}) => {
		setLoading(true);

		const response = await createFunction(token, "create", newData);
		if(response){
			const { id } = response;
			
			const dataToSet = {
				id,
				...newData,
				...extraData,
			};

			const updatedData = [dataToSet, ...backupData];
			const newDataUpdated = specialFilterFunction ? specialFilterFunction(updatedData) : updatedData;
			setData(newDataUpdated);
			setBackupData(updatedData);

			// Store data by entity type if entityKey is provided
			if (entityKey) {
				if (!localData.current[entityKey]) localData.current[entityKey] = {};
				localData.current[entityKey][id] = updatedData;
			}
		}       

		setLoading(false);

		return response ? true : false;
	};

	const updateData = (dataToUpdate) => {
		const { id } = dataToUpdate;
		
		const index = backupData.findIndex(c => c.id === id);

		if(index === -1) return;

		const updatedData = [...backupData];
		const dataUpgraded = {...updatedData[index], ...dataToUpdate};
		updatedData[index] = dataUpgraded;       
		const newDataUpdated = specialFilterFunction ? specialFilterFunction(updatedData) : updatedData;       
		setData(newDataUpdated);
		setBackupData(updatedData);

		// Store data by entity type if entityKey is provided
		if (entityKey) {
			if (!localData.current[entityKey]) localData.current[entityKey] = {};
			localData.current[entityKey][id] = updatedData;
		}
	};
	
	const importData = async (dataToImport) => {
		setLoading(true);
		
		const response = await createFunction(token, "massive_create", dataToImport);
		if (response) {
			// Get current data as base
			const updatedData = [...backupData];
			
			// Process successful imports
			if (response.success && response.success.length > 0) {
				// Add or update items based on successful imports
				for (const successItem of response.success) {
					const { id, tid, data } = successItem;
					const originalData = dataToImport.entities.find(item => item.tid === tid);
					
					if (originalData) {
						const newItem = {
							...originalData,
							id, // Use the returned ID from server
							name: data.name,
							entity_company_id: data.entity_company_id,
							address: data.address,
						};
						
						updatedData.push(newItem);
					}
				}
			}
			
			// Filter data if needed
			const newDataUpdated = specialFilterFunction ? specialFilterFunction(updatedData) : updatedData;
			setData(newDataUpdated);
			setBackupData(updatedData);
			
			// Store data by entity type if entityKey is provided
			if (entityKey && company && company.id) {
				if (!localData.current[entityKey]) localData.current[entityKey] = {};
				localData.current[entityKey][company.id] = updatedData;
			}
		}
		
		setLoading(false);
		
		return response ? { 
			success: response.success || [], 
			errors: response.errors || [] 
		} : false;
	};

	useEffect(() => {
		if(!isCompanySelected(company)){
			message.info("Porfavor selecciona una empresa");
			return;
		}

		const { id } = company;

		// If entityKey is provided, check for specific entity data
		if(entityKey && localData.current[entityKey] && localData.current[entityKey][id] != null){
			if (!localData.current[entityKey]) localData.current[entityKey] = {};

			const newDataUpdated = specialFilterFunction ? specialFilterFunction(localData.current[entityKey][id]) : localData.current[entityKey][id];
			setData(newDataUpdated);
			setBackupData(localData.current[entityKey][id]);
			return;
		}

		// If entityKey is not provided, fall back to the existing behavior
		if (!entityKey && localData.current[id]) {
			const newDataUpdated = specialFilterFunction ? specialFilterFunction(localData.current[id]) : localData.current[id];
			setData(newDataUpdated);
			setBackupData(localData.current[id]);
			return;
		}

		const fetchData = async () => {
			setLoading(true);

			const baseParams = [token, id];

			const additionalParams = aditionalParamsForFetch
				? Object.values(aditionalParamsForFetch).filter(param => param !== undefined)
				: [];
			
			const response = await fetchFunction.apply(null, [...baseParams, ...additionalParams]);

			if(response){
				const newDataUpdated = specialFilterFunction ? specialFilterFunction(response) : response;
				setData(newDataUpdated);
				setBackupData(response);

				// Store data by entity type if entityKey is provided
				if (entityKey) {
					if (!localData.current[entityKey]) localData.current[entityKey] = {};
					localData.current[entityKey][id] = response;
				} else {
					localData.current[id] = response;
				}
			}

			setLoading(false);
		};

		fetchData();
	}, [company, entityKey]);

	return {
		data,
		setData,
		backupData,
		loading,
		addNewData,
		updateData,
		importData,
	};
};